import { useCallback, useRef, useState } from "react"

import { booleanToYesNo } from "./postBodyGenerators"
import { createAcuDeviceDraftPostBody } from "../../../../../components/devices/lct/formSpecs/acuSpec"
import { createEbDeviceDraftPostBody } from "../../../../../components/devices/lct/formSpecs/ebSpec"
import { createEVDCDeviceDraftPostBody } from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { createHpDeviceDraftPostBody } from "../../../../../components/devices/lct/formSpecs/hpSpec"
import {
    DeviceFormData,
    EVACDeviceFormData,
    isLctFormDataAcu,
    isLctFormDataEb,
    isLctFormDataEvac,
    isLctFormDataEvdc,
    isLctFormDataHp,
} from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { SAVE_DRAFT_SUCCESS } from "../../../../../constants/messages"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import {
    snackbar,
    getTsIoMapper,
} from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import { TSaveEVACDeviceDraftPostBody } from "../../../types/devices/lct/publishEvacDeviceTypes"
import { CreateDraftDeviceAPIResponse } from "../../../types/devices/lct/publishLctGeneralTypes"
import { SAVE_LCT_DEVICE_DRAFT } from "../../../urls/private"

const saveLCTDeviceDraftMapper = getTsIoMapper(
    CreateDraftDeviceAPIResponse,
    "saveLCTDeviceDraftMapper"
)

const parseNumberField = (value: number | "" | null): number | undefined => {
    const parsedEmptyString = value === "" ? undefined : value
    return parsedEmptyString ?? undefined
}

const createEVACDeviceDraftPostBody = (
    formData: EVACDeviceFormData,
    attachmentUUID: string,
    isAmendment: boolean,
    systemReference?: string
): TSaveEVACDeviceDraftPostBody => ({
    device_type: "EVAC",
    device_data: {
        SystemReference: systemReference,
        ManufacturerID: formData.manufacturerId ?? undefined,
        Make: formData.deviceMake,
        Model: formData.deviceModel,
        ModelReference: formData.deviceModelRef,
        BarcodeNumber: formData.barcodeNumber,
        Phases: parseNumberField(formData.phases),
        Voltage: parseNumberField(formData.voltage),
        MaxCurrentAmpPerPhase: parseNumberField(formData.maxCurrentAmpPerPhase),
        ACOnlyOutput:
            typeof formData.acOnlyOutput === "boolean"
                ? booleanToYesNo(formData.acOnlyOutput)
                : undefined,
        V2G_Capability:
            typeof formData.v2gCapability === "boolean"
                ? booleanToYesNo(formData.v2gCapability)
                : undefined,
        V2G_ExportCapacity: parseNumberField(formData.v2gExportCapacity),
        V2G_CompletionDate: undefined,
        V2G_G99GB_G99NI_Both: formData.v2gG99gbG999niBoth ?? undefined,
        V2G_TTRStatus: formData.v2gTtrStatus,
        Comments: formData.comments,
        Attachment_UUID: attachmentUUID,
    },
    is_amendment: isAmendment,
})

interface IUseSaveLCTDeviceDraftOutput<T> {
    saveLCTDeviceDraft: (formData: T) => Promise<void>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

const useSaveLCTDeviceDraft = <T extends DeviceFormData>(
    isAmendment: boolean,
    dataset: LCTOnlyDatasets,
    systemReference?: string,
    attachmentUUID?: string
): IUseSaveLCTDeviceDraftOutput<T> => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)
    const apiSystemReference = useRef<string | undefined>(systemReference)

    const saveLCTDeviceDraft = useCallback(
        async (formData: T): Promise<void> => {
            if (!attachmentUUID) {
                return
            }

            let postBody = {}
            if (isLctFormDataEvac(formData, dataset)) {
                postBody = createEVACDeviceDraftPostBody(
                    formData,
                    attachmentUUID,
                    isAmendment,
                    systemReference || apiSystemReference.current || undefined
                )
            } else if (isLctFormDataEvdc(formData, dataset)) {
                postBody = createEVDCDeviceDraftPostBody(
                    formData,
                    attachmentUUID,
                    isAmendment,
                    systemReference || apiSystemReference.current || undefined
                )
            } else if (isLctFormDataHp(formData, dataset)) {
                postBody = createHpDeviceDraftPostBody(
                    formData,
                    attachmentUUID,
                    isAmendment,
                    systemReference || apiSystemReference.current || undefined
                )
            } else if (isLctFormDataEb(formData, dataset)) {
                postBody = createEbDeviceDraftPostBody(
                    formData,
                    attachmentUUID,
                    isAmendment,
                    systemReference || apiSystemReference.current || undefined
                )
            } else if (isLctFormDataAcu(formData, dataset)) {
                postBody = createAcuDeviceDraftPostBody(
                    formData,
                    attachmentUUID,
                    isAmendment,
                    systemReference || apiSystemReference.current || undefined
                )
            } else {
                snackbar(
                    `Couldn't save draft, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
                return
            }

            try {
                setIsLoading(true)
                const response = await postClientProtected(
                    SAVE_LCT_DEVICE_DRAFT,
                    postBody
                )
                const mappedRes = saveLCTDeviceDraftMapper.map(response)
                apiSystemReference.current = mappedRes.SystemReference
                snackbar(SAVE_DRAFT_SUCCESS, "success")
                setIsLoaded(true)
            } catch {
                const appError = invalidApiDataError("Couldn't save draft")
                setError(appError)
                snackbar(
                    `Couldn't save draft, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
            } finally {
                setIsLoading(false)
            }
        },
        [attachmentUUID, isAmendment, systemReference, dataset]
    )

    return {
        saveLCTDeviceDraft,
        isLoaded,
        isLoading,
        error,
    }
}

export default useSaveLCTDeviceDraft
